// =============================================================================
// Origin UI — src/pages/leave/TeamCalendarPage.tsx
// Team Calendar with optional Balances tab for managers.
// - Employees see calendar only (their peers + manager).
// - Managers see Calendar + Balances tabs.
// - HR Admin sees calendar with filters (uses Leave Reports for balances).
// Phase 6 — Leave Management module.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaveApi, type TeamCalendarEntry, type PublicHoliday, type TeamMember, type LeaveBalanceReportItem } from '@/services/leaveApi';
import { api } from '@/services/apiService';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import styles from './leave.module.css';

const leaveTypeColors: Record<string, string> = {
  ANN: '#3B82F6', SCK: '#EF4444', UNP: '#6B7280',
  CMP: '#8B5CF6', MAT: '#EC4899', PAT: '#06B6D4', HSP: '#F59E0B',
  TOIL: '#10B981', BO: '#F97316',
};
const leaveTypeBg: Record<string, string> = {
  ANN: '#DBEAFE', SCK: '#FEE2E2', UNP: '#F3F4F6',
  CMP: '#EDE9FE', MAT: '#FCE7F3', PAT: '#CFFAFE', HSP: '#FEF3C7',
  TOIL: '#D1FAE5', BO: '#FFEDD5',
};

interface RefItem { name: string; id: number; }

// Local YYYY-MM-DD key. Avoids the UTC shift that date.toISOString() introduces
// for users east of UTC (e.g. +04 Dubai), which made holidays and leave bars land
// on the wrong day.
function toKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type ActiveTab = 'calendar' | 'balances';

export function TeamCalendarPage() {
  const [calendarEntries, setCalendarEntries] = useState<TeamCalendarEntry[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [departments, setDepartments] = useState<RefItem[]>([]);
  const [offices, setOffices] = useState<RefItem[]>([]);
  const [managers, setManagers] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [error, setError] = useState('');
  const [filterDept, setFilterDept] = useState<number | ''>('');
  const [filterOffice, setFilterOffice] = useState<number | ''>('');
  const [filterManager, setFilterManager] = useState<string>('');
  const { roles } = useAuth();
  const navigate = useNavigate();

  const [viewDate, setViewDate] = useState(new Date());

  // Balances tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('calendar');
  const [isManager, setIsManager] = useState(false);
  const [teamBalances, setTeamBalances] = useState<LeaveBalanceReportItem[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesLoaded, setBalancesLoaded] = useState(false);
  const [balancesError, setBalancesError] = useState('');

  useEffect(() => { loadRefData(); checkIfManager(); }, [roles.isHRAdmin]);
  useEffect(() => { loadData(); }, [viewDate, filterDept, filterOffice, filterManager]);

  async function checkIfManager() {
    try {
      const result = await leaveApi.getTeamBalances();
      setIsManager(true);
      setTeamBalances(result);
      setBalancesLoaded(true);
    } catch {
      // 403 or error = not a manager — that's fine
      setIsManager(false);
    }
  }

  async function loadRefData() {
    try {
      const [d, o] = await Promise.all([
        api.get<any[]>('/api/admin/departments'),
        api.get<any[]>('/api/admin/office-locations'),
      ]);
      setDepartments(d.filter((x: any) => x.isActive).map((x: any) => ({ name: x.name, id: x.departmentId })));
      setOffices(o.filter((x: any) => x.isActive).map((x: any) => ({ name: x.name, id: x.officeLocationId })));

      // Load managers list from dedicated endpoint
      if (roles.isHRAdmin) {
        try {
          const mgrs = await api.get<{ employeeId: string; name: string; employeeNumber: string; department: string | null }[]>('/api/employees/managers');
          setManagers(mgrs.map(m => ({ id: m.employeeId, name: m.name })).sort((a, b) => a.name.localeCompare(b.name)));
        } catch { /* manager filter optional */ }
      }
    } catch { /* reference data optional */ }
  }

  async function loadData() {
    try {
      if (!initialLoaded) setLoading(true);
      setError('');
      const startOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
      const endOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
      const deptParam = filterDept || undefined;
      const officeParam = filterOffice || undefined;
      const managerParam = filterManager || undefined;

      const [cal, members, hol] = await Promise.all([
        leaveApi.getTeamCalendar(toKey(startOfMonth), toKey(endOfMonth), deptParam, officeParam, managerParam),
        leaveApi.getTeamMembers(deptParam, officeParam, managerParam),
        leaveApi.getHolidays(viewDate.getFullYear()),
      ]);
      setCalendarEntries(cal);
      setTeamMembers(members);
      setHolidays(hol);
    } catch (err: any) { setError(err.message || 'Failed to load team data'); }
    finally { setLoading(false); setInitialLoaded(true); }
  }

  async function loadBalances() {
    if (balancesLoaded) return;
    try {
      setBalancesLoading(true); setBalancesError('');
      const result = await leaveApi.getTeamBalances();
      setTeamBalances(result);
      setBalancesLoaded(true);
    } catch (err: any) { setBalancesError(err.message || 'Failed to load team balances'); }
    finally { setBalancesLoading(false); }
  }

  function handleTabChange(tab: ActiveTab) {
    setActiveTab(tab);
    if (tab === 'balances' && !balancesLoaded) loadBalances();
  }

  function prevMonth() { setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1)); }
  function nextMonth() { setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1)); }
  const monthLabel = viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  // Build calendar grid
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: Date[] = [];
  for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));

  const holidayDates = new Set(
    holidays
      .filter(h => !filterOffice || h.officeLocationId === filterOffice)
      .map(h => h.date.split('T')[0])
  );
  function isWeekend(date: Date) { const day = date.getDay(); return day === 0 || day === 6; }
  function isHoliday(date: Date) { return holidayDates.has(toKey(date)); }

  // Build employee map from ALL team members (not just those with leave)
  const entryMap: Record<string, TeamCalendarEntry[]> = {};
  calendarEntries.forEach(e => {
    if (!entryMap[e.employeeId]) entryMap[e.employeeId] = [];
    entryMap[e.employeeId].push(e);
  });

  const allEmployees = teamMembers.map(m => ({
    id: m.employeeId, name: m.employeeName, number: m.employeeNumber,
    entries: entryMap[m.employeeId] || []
  }));

  function getLeaveForDay(entries: TeamCalendarEntry[], date: Date): TeamCalendarEntry | null {
    const dateStr = toKey(date);
    return entries.find(e => dateStr >= e.startDate.split('T')[0] && dateStr <= e.endDate.split('T')[0]) || null;
  }

  // HR Admin can click a leave bar to open the request detail page (to revoke etc.)
  function openRequest(leaveRequestId: string) {
    if (!roles.isHRAdmin) return;
    navigate(`/leave/requests/${leaveRequestId}`);
  }

  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Show tabs only if user is a manager (not just HRAdmin-without-reports)
  const showTabs = isManager;

  if (loading) return <LoadingSpinner message="Loading team calendar..." />;

  return (
    <div className={styles.page}>
      <PageHeader title="Team Calendar" subtitle="Manage approvals and view team availability" />
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Info link to Approvals Inbox */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <a href="/approvals" style={{ fontSize: '13px', color: 'var(--turquoise-dark)', fontWeight: 600, textDecoration: 'none' }}>
          Manage approvals & delegations →
        </a>
      </div>

      {/* ─── Tabs (managers only) ─── */}
      {showTabs && (
        <div className={styles.tabs} style={{ marginBottom: 16 }}>
          <button className={`${styles.tab} ${activeTab === 'calendar' ? styles.tabActive : ''}`} onClick={() => handleTabChange('calendar')}>
            Calendar
          </button>
          <button className={`${styles.tab} ${activeTab === 'balances' ? styles.tabActive : ''}`} onClick={() => handleTabChange('balances')}>
            Team Balances
          </button>
        </div>
      )}

      {/* ─── Calendar Tab ─── */}
      {activeTab === 'calendar' && (
        <Card padding={false}>
          <div className={styles.calendarHeader} style={{ padding: '16px 20px' }}>
            <button className={styles.btnSecondary} onClick={prevMonth}>◀ Prev</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {roles.isHRAdmin && (
                <>
                  <select className={styles.filterSelect} value={filterDept} onChange={e => setFilterDept(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">All departments</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <select className={styles.filterSelect} value={filterOffice} onChange={e => setFilterOffice(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">All offices</option>
                    {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                  <select className={styles.filterSelect} value={filterManager} onChange={e => setFilterManager(e.target.value)}>
                    <option value="">All managers</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </>
              )}
              <h3 className={styles.monthTitle}>{monthLabel}</h3>
            </div>
            <button className={styles.btnSecondary} onClick={nextMonth}>Next ▶</button>
          </div>

          {allEmployees.length === 0 ? (
            <p className={styles.muted} style={{ padding: '24px', textAlign: 'center' }}>No team members found.</p>
          ) : (
            <div className={styles.gridContainer}>
              <div className={styles.gridWrapper}>
                <table className={styles.gridTable}>
                  <thead>
                    <tr>
                      <th className={styles.gridNameCol}>Employee</th>
                      {days.map(d => {
                        const isWe = isWeekend(d); const isHol = isHoliday(d);
                        const isToday = d.getTime() === today.getTime();
                        return (
                          <th key={d.getDate()} className={`${styles.gridDayCol} ${isWe ? styles.gridWeekend : ''} ${isHol ? styles.gridHoliday : ''} ${isToday ? styles.gridToday : ''}`}>
                            <div className={styles.gridDayName}>{d.toLocaleDateString('en-GB', { weekday: 'narrow' })}</div>
                            <div className={styles.gridDayNum}>{d.getDate()}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {allEmployees.map((emp) => (
                      <tr key={emp.id}>
                        <td className={styles.gridNameCell}>
                          <div className={styles.gridEmpName}>{emp.name}</div>
                          <div className={styles.gridEmpNum}>{emp.number}</div>
                        </td>
                        {days.map(d => {
                          const isWe = isWeekend(d); const isHol = isHoliday(d);
                          const isToday = d.getTime() === today.getTime();
                          const leave = getLeaveForDay(emp.entries, d);
                          let cellClass = styles.gridCell;
                          if (isWe) cellClass += ` ${styles.gridWeekend}`;
                          if (isHol) cellClass += ` ${styles.gridHoliday}`;
                          if (isToday) cellClass += ` ${styles.gridToday}`;

                          if (leave && !isWe && !isHol) {
                            const bg = leaveTypeBg[leave.leaveTypeCode] || '#F3F4F6';
                            const border = leaveTypeColors[leave.leaveTypeCode] || '#6B7280';
                            const isStart = toKey(d) === leave.startDate.split('T')[0];
                            const isEnd = toKey(d) === leave.endDate.split('T')[0];
                            const isPending = leave.status === 'Submitted';
                            const clickable = roles.isHRAdmin;
                            return (
                              <td key={d.getDate()} className={cellClass}>
                                <div className={`${styles.gridLeaveBar} ${isPending ? styles.gridLeaveBarPending : ''}`}
                                  onClick={clickable ? () => openRequest(leave.leaveRequestId) : undefined}
                                  style={{ background: bg, borderTop: `2px solid ${border}`,
                                    borderLeft: isStart ? `3px solid ${border}` : 'none',
                                    borderRight: isEnd ? `3px solid ${border}` : 'none',
                                    borderRadius: `${isStart ? '4px' : '0'} ${isEnd ? '4px' : '0'} ${isEnd ? '4px' : '0'} ${isStart ? '4px' : '0'}`,
                                    cursor: clickable ? 'pointer' : 'default',
                                  }}
                                  title={`${leave.leaveTypeName} (${leave.status})${clickable ? ' — click to manage' : ''}`}>
                                  {isStart && <span className={styles.gridLeaveCode}>{leave.leaveTypeCode}</span>}
                                </div>
                              </td>
                            );
                          }
                          return <td key={d.getDate()} className={cellClass}></td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className={styles.gridLegend}>
            {Object.entries(leaveTypeColors).filter(([code]) => calendarEntries.some(e => e.leaveTypeCode === code)).map(([code, color]) => (
              <div key={code} className={styles.legendItem}>
                <div className={styles.legendSwatch} style={{ background: leaveTypeBg[code], borderTop: `2px solid ${color}` }} /><span>{code}</span>
              </div>
            ))}
            <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: '#F3F4F6' }} /><span>Weekend</span></div>
            <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: '#FEF3C7' }} /><span>Holiday</span></div>
            <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: '#DBEAFE', backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.6) 3px, rgba(255,255,255,0.6) 6px)' }} /><span>Pending</span></div>
          </div>
        </Card>
      )}

      {/* ─── Balances Tab ─── */}
      {activeTab === 'balances' && (
        <TeamBalancesTable data={teamBalances} loading={balancesLoading} error={balancesError} onDismissError={() => setBalancesError('')} />
      )}
    </div>
  );
}

// =============================================================================
// Team Balances Table Component — matches HR Leave Reports balance format
// =============================================================================

function TeamBalancesTable({ data, loading, error, onDismissError }: {
  data: LeaveBalanceReportItem[];
  loading: boolean;
  error: string;
  onDismissError: () => void;
}) {
  if (loading) return <LoadingSpinner message="Loading team balances..." />;
  if (error) return <ErrorBanner message={error} onDismiss={onDismissError} />;
  if (data.length === 0) return <Card><p className={styles.muted} style={{ textAlign: 'center' }}>No direct reports found.</p></Card>;

  // Helper: get ANN+BO+TOIL merged balance for an employee (same logic as LeaveReportsPage)
  function getMergedBalance(emp: LeaveBalanceReportItem, code: string) {
    const b = emp.balances.find(bal => bal.leaveTypeCode === code);
    if (code === 'ANN') {
      const bo = emp.balances.find(bal => bal.leaveTypeCode === 'BO');
      const toil = emp.balances.find(bal => bal.leaveTypeCode === 'TOIL');
      const base = b || { entitlement: 0, carryForward: 0, taken: 0, committed: 0, pending: 0, available: 0, leaveTypeCode: 'ANN', leaveTypeName: 'Annual Leave' };
      const boEnt = bo ? bo.entitlement : 0;
      const boCf = bo ? bo.carryForward : 0;
      const boTaken = bo ? bo.taken : 0;
      const boCommitted = bo ? bo.committed : 0;
      const boPending = bo ? bo.pending : 0;
      const boAvail = bo ? bo.available : 0;
      const toilEnt = toil ? toil.entitlement : 0;
      const toilCf = toil ? toil.carryForward : 0;
      const toilTaken = toil ? toil.taken : 0;
      const toilCommitted = toil ? toil.committed : 0;
      const toilPending = toil ? toil.pending : 0;
      const toilAvail = toil ? toil.available : 0;
      const entitlement = base.entitlement + boEnt + toilEnt;
      const carryForward = base.carryForward + boCf + toilCf;
      return {
        leaveTypeCode: 'ANN', leaveTypeName: base.leaveTypeName,
        entitlement, carryForward,
        totalDue: entitlement + carryForward,
        taken: base.taken + boTaken + toilTaken,
        committed: base.committed + boCommitted + toilCommitted,
        pending: base.pending + boPending + toilPending,
        available: base.available + boAvail + toilAvail,
        hasBo: boEnt > 0, boEntitlement: boEnt,
        hasToil: toilEnt > 0 || toilTaken > 0, toilEntitlement: toilEnt, toilTaken,
      };
    }
    if (!b) return null;
    return { ...b, totalDue: b.entitlement + b.carryForward, hasBo: false, boEntitlement: 0, hasToil: false, toilEntitlement: 0, toilTaken: 0 };
  }

  // Compute dynamic leave type columns — exclude BO and TOIL (merged into ANN)
  const leaveTypeCodes: { code: string; name: string }[] = [];
  const seen = new Set<string>();
  for (const emp of data) {
    for (const b of emp.balances) {
      if (b.leaveTypeCode === 'BO' || b.leaveTypeCode === 'TOIL') continue;
      if (!seen.has(b.leaveTypeCode) && b.entitlement > 0) {
        seen.add(b.leaveTypeCode);
        leaveTypeCodes.push({ code: b.leaveTypeCode, name: b.leaveTypeName });
      }
    }
  }
  leaveTypeCodes.sort((a, b) => {
    if (a.code === 'ANN') return -1;
    if (b.code === 'ANN') return 1;
    return a.code.localeCompare(b.code);
  });

  const sorted = [...data].sort((a, b) => a.employeeName.localeCompare(b.employeeName));

  return (
    <Card padding={false}>
      <div className={styles.gridContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Office</th>
              {leaveTypeCodes.map(lt => (
                <th key={lt.code} style={{ textAlign: 'right' }}>{lt.code}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(emp => (
              <tr key={emp.employeeId}>
                <td><strong>{emp.employeeName}</strong> <span className={styles.muted}>{emp.employeeNumber}</span></td>
                <td>{emp.departmentName}</td>
                <td>{emp.officeName}</td>
                {leaveTypeCodes.map(lt => {
                  const b = getMergedBalance(emp, lt.code);
                  if (!b || b.totalDue === 0) {
                    return <td key={lt.code} style={{ textAlign: 'right' }}><span className={styles.muted}>—</span></td>;
                  }
                  const parts = [`Entitlement: ${b.entitlement}`];
                  if (b.carryForward > 0) parts.push(`Carry Fwd: ${b.carryForward}`);
                  if (b.hasBo) parts.push(`(incl. BO: ${b.boEntitlement})`);
                  if (b.hasToil) parts.push(`(incl. TOIL: ${b.toilEntitlement} ent, ${b.toilTaken} taken)`);
                  parts.push(`Taken: ${b.taken}`, `Committed: ${b.committed}`, `Pending: ${b.pending}`);
                  return (
                    <td key={lt.code} style={{ textAlign: 'right' }} title={parts.join(', ')}>
                      <span style={{ fontWeight: 700, color: b.available < 0 ? '#E53E3E' : 'var(--turquoise-dark)' }}>{b.available}</span>
                      <span className={styles.muted}> / {b.totalDue}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-200)', fontSize: 13, color: 'var(--gray-500)' }}>
        {sorted.length} employee{sorted.length !== 1 ? 's' : ''}. Showing Available / Total Due (Entitlement + Carry Forward). ANN includes Birthday Off and TOIL where applicable. Hover cells for breakdown.
      </div>
    </Card>
  );
}
